import React, { useEffect, useRef, useState } from "react";
import {
  RotateCcw, ArrowRight, Home, Trophy, Skull, Coins, Target,
  Zap, Sparkles, Disc, Dog, Rocket, Cloud, Star, ChevronUp, Plus,
} from "lucide-react";
import { playClick, playWin, playEpicReveal } from "../game/sound";
import { RUN_POWERS } from "../game/upgrades";
import {
  submitLeaderboard,
  incrementStats,
  fetchLeaderboardTop,
} from "../game/leaderboardService";
import { submitRunSummary } from "../game/authService";
import { useAuth } from "../contexts/AuthContext";
import PlayerBadge from "../components/PlayerBadge";
import { toast } from "sonner";

const POWER_ICONS = { Zap, Sparkles, Disc, Dog, Rocket, Cloud };

function RunPowersStrip({ runPowers }) {
  if (!runPowers) return null;
  const owned = Object.keys(runPowers)
    .filter((id) => (runPowers[id] || 0) > 0 && RUN_POWERS[id])
    .map((id) => ({ id, lvl: runPowers[id], meta: RUN_POWERS[id] }))
    .sort((a, b) => b.lvl - a.lvl);
  if (owned.length === 0) return null;
  return (
    <div className="end-powers" data-testid="end-powers">
      <div className="end-powers-label">Your Build</div>
      <div className="end-powers-list">
        {owned.map(({ id, lvl, meta }) => {
          const Icon = POWER_ICONS[meta.icon] || Sparkles;
          return (
            <div key={id} className="end-power-chip" title={meta.name}>
              <Icon size={14} strokeWidth={2.2} />
              <span className="end-power-name">{meta.name}</span>
              <span className="end-power-lvl">Lv {lvl}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Animated XP / level-up summary. Renders nothing if the player is not
// signed in — XP is account-bound.
function XpSummary({ summary }) {
  // summary: { xp_earned, raw_xp, bonus_multiplier, old_level, new_level,
  //   leveled_up, talent_points_earned, xp_into_level, xp_to_next,
  //   start_level, start_into, start_to_next }
  const [animXp, setAnimXp] = useState(0);
  const [stage, setStage] = useState(0); // 0=intro, 1=filling, 2=levelup-burst, 3=done
  const [burstLevels, setBurstLevels] = useState(0); // visible level number during burst

  useEffect(() => {
    if (!summary) return;
    let rafId;
    let start = null;
    const duration = 1100;
    const target = summary.xp_earned || 0;
    setStage(1);
    const tick = (ts) => {
      if (start === null) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimXp(Math.round(target * eased));
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        if (summary.leveled_up) {
          playEpicReveal();
          setStage(2);
          // Reveal new level after a short delay so the burst lands.
          setBurstLevels(summary.old_level);
          let lvl = summary.old_level;
          const tickLvl = () => {
            lvl += 1;
            setBurstLevels(lvl);
            if (lvl < summary.new_level) {
              setTimeout(tickLvl, 280);
            } else {
              setTimeout(() => setStage(3), 600);
            }
          };
          setTimeout(tickLvl, 300);
        } else {
          setStage(3);
        }
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [summary]);

  if (!summary) return null;

  const currentLevel =
    stage >= 2 && burstLevels ? burstLevels : summary.old_level;
  const isFinal = stage >= 3 || !summary.leveled_up;
  const into = isFinal ? summary.xp_into_level : summary.start_into;
  const toNext = isFinal ? summary.xp_to_next : summary.start_to_next;
  // Animate fill % during stage 1 only. After that, lock to final progress.
  const fillBase = Math.max(0, Math.min(1, into / Math.max(1, toNext)));
  const animProgress =
    stage === 1
      ? Math.min(
          1,
          (summary.start_into + animXp) / Math.max(1, summary.start_to_next)
        )
      : fillBase;

  return (
    <div className="end-xp" data-testid="end-xp-summary">
      <div className="end-xp-head">
        <div className="end-xp-headline">
          <Star size={16} /> Account Progression
        </div>
        <div className="end-xp-earned" data-testid="end-xp-earned">
          +{animXp} XP
        </div>
      </div>
      <div className="end-xp-row">
        <div className={`end-xp-level-pill ${stage === 2 ? "burst" : ""}`}>
          <Sparkles size={14} /> Lv {currentLevel}
        </div>
        <div className="end-xp-bar-wrap">
          <div
            className="end-xp-bar-fill"
            style={{ width: `${animProgress * 100}%` }}
          />
          <div className="end-xp-bar-label">
            {into} / {toNext} XP
          </div>
        </div>
      </div>
      {summary.bonus_multiplier > 1 && (
        <div className="end-xp-bonus">
          XP bonus: <b>x{summary.bonus_multiplier.toFixed(2)}</b> (Bonus XP talent)
        </div>
      )}
      {summary.leveled_up && stage >= 2 && (
        <div className="end-xp-burst" data-testid="end-xp-burst">
          <div className="end-xp-burst-title">
            <ChevronUp size={20} /> LEVEL UP!
          </div>
          <div className="end-xp-burst-sub">
            Lv {summary.old_level} <ArrowRight size={14} /> Lv {summary.new_level}
          </div>
          {summary.talent_points_earned > 0 && (
            <div
              className="end-xp-points"
              data-testid="end-xp-points-earned"
            >
              <Plus size={14} /> {summary.talent_points_earned} Talent Point
              {summary.talent_points_earned === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EndScreen({
  type, level, stats, runPowers, isLastLevel, onRetry, onNext, onMenu,
}) {
  const { user, refresh } = useAuth();
  const [rank, setRank] = useState(null);
  const [xpSummary, setXpSummary] = useState(null);
  const [xpFailed, setXpFailed] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const submittedRef = useRef(false);

  // Count how many distinct upgrades the player would lose by going to menu
  // right now. Used to gate the "Are you sure?" prompt — we only ask if
  // there's something to lose AND there's still a way to keep playing
  // (wave/win/victory states have a "Next" button; lose state already lost).
  const upgradeCount = runPowers
    ? Object.values(runPowers).filter((v) => v > 0).length
    : 0;
  const isWin     = type === "win";
  const isWave    = type === "wave";
  const isVictory = type === "victory";
  const canContinue = isWin || isWave || isVictory;
  const shouldConfirmLeave = canContinue && upgradeCount > 0;

  const handleMenuClick = () => {
    playClick();
    if (shouldConfirmLeave) {
      setConfirmLeave(true);
    } else {
      onMenu();
    }
  };

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    let cancelled = false;
    // 8s fallback so the loader never sticks forever on a transient failure.
    const timeout = setTimeout(() => {
      if (!cancelled && !xpSummary) setXpFailed(true);
    }, 8000);
    const submit = async () => {
      const entry = await submitLeaderboard({
        wave: level.wave || 1,
        score: stats.score || 0,
        levelId: level.id || 1,
        zombies: stats.zombiesCollected || 0,
      });
      incrementStats({ zombies: stats.zombiesCollected || 0, runs: 1 });
      if (entry) {
        const top = await fetchLeaderboardTop(10);
        if (!cancelled) {
          const idx = top.findIndex((e) => e.id === entry.id);
          if (idx >= 0) setRank(idx + 1);
        }
      }
      // Phase 4: account XP / lifetime stats. Only for signed-in users.
      if (user) {
        const startLevel = user.level || 1;
        const startInto = user.xp_into_level || 0;
        const startToNext = user.xp_to_next || 1;
        const summary = await submitRunSummary({
          wave: level.wave || 1,
          zombies: stats.zombiesCollected || 0,
          bossesDefeated: stats.bossesDefeated || 0,
          completed: !!stats.completed,
          playSeconds: stats.playSeconds || 0,
          coinsEarned: stats.coinsEarned || 0,
        });
        if (summary && !cancelled) {
          setXpSummary({
            xp_earned: 0,
            raw_xp: 0,
            bonus_multiplier: 1,
            old_level: startLevel,
            new_level: startLevel,
            leveled_up: false,
            talent_points_earned: 0,
            xp_into_level: startInto,
            xp_to_next: startToNext || 1,
            ...summary,
            start_level: startLevel,
            start_into: startInto,
            start_to_next: startToNext || 1,
          });
          // Refresh AuthContext so Menu reflects new XP/level/talent_points.
          refresh();
          // Notify newly unlocked achievements.
          const newly = Array.isArray(summary.newly_unlocked_achievements)
            ? summary.newly_unlocked_achievements
            : [];
          if (newly.length > 0) {
            const n = newly.length;
            toast.success(
              n === 1
                ? "Achievement unlocked!"
                : `${n} achievements unlocked!`,
              {
                description: "Open the Achievements screen to claim your rewards.",
                duration: 6000,
              }
            );
          }
        } else if (!cancelled) {
          setXpFailed(true);
        }
      }
    };
    submit();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, []);

  const title =
    type === "victory"
      ? "You Cleared The Outbreak!"
      : isWave
      ? `Wave ${stats.wave || 1} Complete!`
      : isWin
      ? "Level Complete!"
      : "Outbreak Continues...";
  const subtitle =
    type === "victory"
      ? `Endless mode unlocked — Wave ${stats.nextWave || 2} is harder.`
      : isWave
      ? `Wave ${stats.nextWave || 2} incoming — more zombies, less time.`
      : isWin
      ? "Nice flying, pilot."
      : "The timer ran out. Try again!";

  return (
    <div className="screen end-screen" data-testid={`end-screen-${type}`}>
      <div className={`end-card end-${type}`}>
        {/* Small Menu exit in the top-right corner — moved out of the
            primary-action area so mobile users can't mis-tap it while
            reaching for "Next Wave" / "Retry" (which used to be siblings). */}
        <button
          type="button"
          className="end-menu-corner"
          onClick={handleMenuClick}
          data-testid="end-menu-button"
          aria-label="Return to menu"
          title="Return to menu"
        >
          <Home size={16} />
        </button>
        <div className="end-icon">
          {type === "victory" ? <Trophy size={48} /> : isWin ? <Trophy size={40} /> : <Skull size={40} />}
        </div>
        <h2 className="end-title">{title}</h2>
        <p className="end-sub">{subtitle}</p>

        {rank && (
          <div className="end-rank" data-testid="end-rank">
            <Trophy size={16} /> You&apos;re #{rank} on the global leaderboard!
            <PlayerBadge isFounder={!!user?.founder} isVip={!!user?.is_vip} />
          </div>
        )}

        <div className="end-stats">
          <div className="end-stat">
            <Target size={16} />
            <span className="end-stat-label">Zombies</span>
            <span className="end-stat-value" data-testid="end-zombies">{stats.zombiesCollected}</span>
          </div>
          <div className="end-stat">
            <Trophy size={16} />
            <span className="end-stat-label">Score</span>
            <span className="end-stat-value" data-testid="end-score">{stats.score}</span>
          </div>
          <div className="end-stat">
            <Coins size={16} />
            <span className="end-stat-label">Coins</span>
            <span className="end-stat-value" data-testid="end-coins">+{stats.coinsEarned}</span>
          </div>
        </div>

        {user && xpSummary && <XpSummary summary={xpSummary} />}
        {user && !xpSummary && !xpFailed && (
          <div className="end-xp-loading end-xp-reserve" data-testid="end-xp-loading">
            <Star size={14} /> Calculating XP...
          </div>
        )}
        {!user && <div className="end-xp-reserve" aria-hidden="true" />}

        <RunPowersStrip runPowers={runPowers} />

        <div className="end-buttons">
          {!isVictory && (isWin || isWave) && (
            <button
              type="button"
              className="zc-btn primary"
              onClick={() => { playClick(); onNext(); }}
              data-testid="end-next-button"
            >
              {isWave ? `Start Wave ${stats.nextWave || 2}` : "Next Level"} <ArrowRight size={16} />
            </button>
          )}
          {isVictory && (
            <button
              type="button"
              className="zc-btn primary"
              onClick={() => { playClick(); onNext(); }}
              data-testid="end-victory-continue-button"
            >
              Continue Endless <ArrowRight size={16} />
            </button>
          )}
          {!isWin && !isVictory && (
            <button
              type="button"
              className="zc-btn primary"
              onClick={() => { playClick(); onRetry(); }}
              data-testid="end-retry-button"
            >
              <RotateCcw size={16} /> Retry
            </button>
          )}
        </div>
      </div>
      {confirmLeave && (
        <div
          className="end-confirm-overlay"
          data-testid="end-confirm-leave-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Backdrop tap = cancel (treat as "Stay")
            if (e.target === e.currentTarget) {
              playClick();
              setConfirmLeave(false);
            }
          }}
        >
          <div className="end-confirm-card">
            <div className="end-confirm-icon">
              <Skull size={40} />
            </div>
            <h3 className="end-confirm-title">Leave run?</h3>
            <p className="end-confirm-msg">
              You&apos;ll lose your <b>{upgradeCount}</b> upgrade{upgradeCount === 1 ? "" : "s"}.
              {(isWin || isWave) && " You can keep playing for free."}
            </p>
            <div className="end-confirm-buttons">
              <button
                type="button"
                className="zc-btn primary"
                onClick={() => { playClick(); setConfirmLeave(false); }}
                data-testid="end-confirm-stay"
                autoFocus
              >
                Stay
              </button>
              <button
                type="button"
                className="zc-btn ghost end-confirm-leave"
                onClick={() => { playClick(); setConfirmLeave(false); onMenu(); }}
                data-testid="end-confirm-leave"
              >
                Yes, leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
